import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Save, Sparkles, Loader2, Wand2, RefreshCw,
    CheckCircle2, AlertTriangle, Send, ExternalLink, Image as ImageIcon, Film,
} from "lucide-react";
import { toast } from "sonner";

import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import ProductVisionPanel from "./studio/ProductVisionPanel";
import CalendarPreviewEditor from "./studio/CalendarPreviewEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
    studioHeaders, isStudioAuthed, clearStudioPassword,
} from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SECTION_TABS = [
    { key: "brand_voice", label: "Brand Voice" },
    { key: "calendar_30d", label: "30-Day Calendar" },
    { key: "captions", label: "Captions" },
    { key: "hooks", label: "Hooks" },
    { key: "reels", label: "Reel Scripts" },
    { key: "visual_guide", label: "Visual Guide" },
];

// ---------- Small helpers ----------
/* Re-assign this kit order to another member email so the right member
   sees the content in their member hub. */
function TransferOrder({ orderId, currentEmail, onDone }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!email.trim()) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/orders/${orderId}/transfer`,
                { email: email.trim() }, { headers: studioHeaders() });
            toast.success(`Order transferred to ${email.trim()}${data.member_exists ? " (member account exists)" : " — no member account yet, they'll need to register"}`);
            setOpen(false); setEmail("");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Transfer failed");
        } finally { setBusy(false); }
    };
    return (
        <span className="inline-block">
            {!open ? (
                <button onClick={() => setOpen(true)} data-testid="transfer-order-btn"
                    className="text-[10px] uppercase tracking-[0.14em] font-bold text-cafe-olive hover:text-cafe-espresso underline underline-offset-2 transition-colors">
                    Transfer to member →
                </button>
            ) : (
                <span className="inline-flex items-center gap-2 flex-wrap">
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                        placeholder={currentEmail || "member@email.com"} data-testid="transfer-email-input"
                        className="rounded-full border border-border bg-white px-3 py-1.5 text-xs w-56 focus:border-cafe-olive focus:outline-none" />
                    <button onClick={submit} disabled={busy || !email.trim()} data-testid="transfer-confirm-btn"
                        className="rounded-full bg-cafe-olive text-cafe-cream px-4 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold disabled:opacity-50">
                        {busy ? "…" : "Transfer"}
                    </button>
                    <button onClick={() => setOpen(false)} className="text-xs text-cafe-mocha hover:text-cafe-espresso">✕</button>
                </span>
            )}
        </span>
    );
}

function StatusBadge({ status }) {
    const map = {
        idle: { tone: "bg-cafe-latte text-cafe-mocha", label: "No draft" },
        generating: { tone: "bg-cafe-terracotta text-cafe-cream animate-pulse", label: "Generating…" },
        ready: { tone: "bg-cafe-olive text-cafe-cream", label: "Draft ready" },
        failed: { tone: "bg-red-700 text-cafe-cream", label: "AI failed" },
    };
    const c = map[status] || map.idle;
    return (
        <span className={cn("text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full", c.tone)}>
            {c.label}
        </span>
    );
}

function setNested(draft, path, value) {
    // path like "brand_voice.summary" or "calendar_30d.0.caption"
    const parts = path.split(".");
    const next = JSON.parse(JSON.stringify(draft));
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        const idx = /^\d+$/.test(k) ? parseInt(k, 10) : k;
        if (cur[idx] === undefined) cur[idx] = /^\d+$/.test(parts[i + 1]) ? [] : {};
        cur = cur[idx];
    }
    const lastKey = parts[parts.length - 1];
    cur[/^\d+$/.test(lastKey) ? parseInt(lastKey, 10) : lastKey] = value;
    return next;
}

// ---------- Section renderers ----------
function BrandVoiceEditor({ data, onChange }) {
    return (
        <div className="space-y-6">
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Voice summary</Label>
                <Textarea
                    rows={9}
                    value={data?.summary || ""}
                    onChange={e => onChange("brand_voice.summary", e.target.value)}
                    className="mt-2 bg-cafe-cream border-border rounded-2xl"
                    data-testid="bv-summary"
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Do say</Label>
                    {(data?.do_say || []).map((s, i) => (
                        <Input
                            key={i}
                            value={s}
                            onChange={e => {
                                const next = [...(data.do_say || [])];
                                next[i] = e.target.value;
                                onChange("brand_voice.do_say", next);
                            }}
                            className="mt-2 bg-cafe-cream border-border rounded-xl"
                        />
                    ))}
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Don't say</Label>
                    {(data?.dont_say || []).map((s, i) => (
                        <Input
                            key={i}
                            value={s}
                            onChange={e => {
                                const next = [...(data.dont_say || [])];
                                next[i] = e.target.value;
                                onChange("brand_voice.dont_say", next);
                            }}
                            className="mt-2 bg-cafe-cream border-border rounded-xl"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function CaptionListEditor({ pathPrefix, items, onChange }) {
    return (
        <div className="space-y-2">
            {(items || []).map((c, i) => (
                <Textarea
                    key={i}
                    rows={4}
                    value={c}
                    onChange={e => {
                        const next = [...items];
                        next[i] = e.target.value;
                        onChange(pathPrefix, next);
                    }}
                    className="bg-cafe-cream border-border rounded-xl text-sm"
                />
            ))}
        </div>
    );
}

function CaptionsEditor({ data, onChange }) {
    const data_ = data || {};
    const buckets = ["educate", "story", "sell", "behind", "community", "calendar"];
    return (
        <div className="space-y-8">
            {buckets.map(b => (
                <div key={b}>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2 block">
                        {b} ({(data_[b] || []).length})
                    </Label>
                    <CaptionListEditor
                        pathPrefix={`captions.${b}`}
                        items={data_[b] || []}
                        onChange={onChange}
                    />
                </div>
            ))}
        </div>
    );
}

function HooksEditor({ data, onChange }) {
    const data_ = data || {};
    const buckets = ["curiosity", "controversial", "list", "story", "question"];
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {buckets.map(b => (
                <div key={b}>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-2 block">
                        {b}
                    </Label>
                    {(data_[b] || []).map((h, i) => (
                        <Input
                            key={i}
                            value={h}
                            onChange={e => {
                                const next = [...(data_[b] || [])];
                                next[i] = e.target.value;
                                onChange(`hooks.${b}`, next);
                            }}
                            className="mb-2 bg-cafe-cream border-border rounded-xl text-sm"
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

function ReelsEditor({ data, onChange }) {
    return (
        <div className="space-y-4">
            {(data || []).map((r, i) => (
                <div key={i} className="bg-cafe-cream rounded-2xl border border-border p-5">
                    <Input
                        value={r.title || ""}
                        onChange={e => onChange(`reels.${i}.title`, e.target.value)}
                        className="bg-transparent border-0 border-b border-border rounded-none px-0 mb-2 font-serif text-lg text-cafe-espresso"
                        placeholder="Reel title"
                    />
                    <Input
                        value={r.hook || ""}
                        onChange={e => onChange(`reels.${i}.hook`, e.target.value)}
                        className="mb-2 bg-cafe-latte border-border rounded-xl text-sm"
                        placeholder="Hook"
                    />
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mt-3 block">Beats</Label>
                    {(r.beats || []).map((b, j) => (
                        <Input
                            key={j}
                            value={b}
                            onChange={e => {
                                const next = [...(r.beats || [])];
                                next[j] = e.target.value;
                                onChange(`reels.${i}.beats`, next);
                            }}
                            className="mb-1 mt-1 bg-cafe-latte border-border rounded-xl text-xs font-mono"
                        />
                    ))}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <Input
                            value={r.voiceover || ""}
                            onChange={e => onChange(`reels.${i}.voiceover`, e.target.value)}
                            className="bg-cafe-latte border-border rounded-xl text-xs"
                            placeholder="Voiceover (optional)"
                        />
                        <Textarea
                            rows={2}
                            value={r.caption || ""}
                            onChange={e => onChange(`reels.${i}.caption`, e.target.value)}
                            className="bg-cafe-latte border-border rounded-xl text-xs"
                            placeholder="Caption"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

function VisualGuideEditor({ data, onChange }) {
    return (
        <div className="space-y-6">
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Mood</Label>
                <Textarea
                    rows={6}
                    value={data?.mood || ""}
                    onChange={e => onChange("visual_guide.mood", e.target.value)}
                    className="mt-2 bg-cafe-cream border-border rounded-2xl"
                />
            </div>
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Palette</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                    {(data?.palette || []).map((hex, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-md border border-border" style={{ background: hex }} />
                            <Input
                                value={hex}
                                onChange={e => {
                                    const next = [...(data.palette || [])];
                                    next[i] = e.target.value;
                                    onChange("visual_guide.palette", next);
                                }}
                                className="w-28 bg-cafe-cream border-border rounded-xl font-mono text-xs"
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Shot list</Label>
                {(data?.shot_list || []).map((s, i) => (
                    <Input
                        key={i}
                        value={s}
                        onChange={e => {
                            const next = [...(data.shot_list || [])];
                            next[i] = e.target.value;
                            onChange("visual_guide.shot_list", next);
                        }}
                        className="mt-2 bg-cafe-cream border-border rounded-xl text-sm"
                    />
                ))}
            </div>
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Phone settings</Label>
                <Textarea
                    rows={4}
                    value={data?.phone_settings || ""}
                    onChange={e => onChange("visual_guide.phone_settings", e.target.value)}
                    className="mt-2 bg-cafe-cream border-border rounded-2xl"
                />
            </div>
            <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-semibold">Reference takeaways</Label>
                <Textarea
                    rows={4}
                    value={data?.reference_takeaways || ""}
                    onChange={e => onChange("visual_guide.reference_takeaways", e.target.value)}
                    className="mt-2 bg-cafe-cream border-border rounded-2xl"
                />
            </div>
        </div>
    );
}

// ---------- IG 9-grid (Nano Banana) panel ----------
function ImagePanel({ order, onGenerate, onRetryFailed }) {
    const status = order?.ai_images_status || "idle";
    const images = order?.ai_images || [];
    const ready = images.filter(i => i?.status === "ready" && i?.url);
    const failed = images.filter(i => i?.status === "failed");
    const budgetExhausted = /insufficient.balance|budget.exceeded/i.test(order?.ai_images_error || "");
    // Expected count = number of images that have started arriving (max of array length, fallback 9)
    const expected = Math.max(images.length, 9);
    const progressPct = expected > 0 ? Math.round((ready.length / expected) * 100) : 0;

    const Status = () => {
        if (status === "generating") {
            return (
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-terracotta text-cafe-cream animate-pulse">
                    <Loader2 className="h-3 w-3 animate-spin" /> Rendering · {ready.length}/{expected}
                </span>
            );
        }
        if (status === "ready") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-olive text-cafe-cream">All {ready.length} ready</span>;
        }
        if (status === "partial") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-amber-700 text-cafe-cream">Partial · {ready.length}/{expected}</span>;
        }
        if (status === "failed") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-red-700 text-cafe-cream">Failed</span>;
        }
        return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-latte text-cafe-mocha">Not generated</span>;
    };

    const canGenerate = status !== "generating";

    return (
        <div className="bg-cafe-cream border border-cafe-olive/30 rounded-2xl p-5 mb-6" data-testid="image-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive font-semibold mb-1 flex items-center gap-2">
                        <ImageIcon className="h-3 w-3" /> IG Grid · AI Images
                    </p>
                    <p className="text-cafe-mocha text-sm">
                        Format-aware: <strong>Single</strong> = 1 AI image · <strong>Carousel</strong> = 5 AI slides · <strong>Reel</strong> = video (rendered in Video panel below, NOT here) · <strong>Story</strong> = no image (story-only days are reposts/reminders). Image-to-image active when buyer uploaded a brand or product reference.
                    </p>
                </div>
                <Status />
            </div>

            {/* Re-generate CTA — surfaced prominently when images exist but
                are broken/lost (typical after pod restart on ephemeral disk,
                or partial-failure batches). Distinct from the "first time"
                count selector below so Kaily doesn't have to guess what
                "Full 30 days" means in a recovery scenario. */}
            {canGenerate && (status === "ready" || status === "partial" || status === "failed") && (
                <div
                    className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid="image-regen-cta"
                >
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-1">
                            {status === "ready" ? "Need to re-render?" : "Recover this batch"}
                        </p>
                        <p className="text-cafe-mocha text-xs leading-relaxed">
                            {status === "ready"
                                ? "Click below to overwrite all existing images with a fresh render (~5–10 min). Use this if customer's kit page shows broken thumbnails (file lost) or you want a different style."
                                : "Some images failed — re-render the whole batch to get a clean delivery. Old images will be replaced."}
                        </p>
                    </div>
                    <Button
                        onClick={() => onGenerate(30)}
                        data-testid="image-regen-30-btn"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs whitespace-nowrap shrink-0"
                    >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-generate 30 images
                    </Button>
                </div>
            )}

            {/* Count selector buttons */}
            {canGenerate && (
                <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-border/60">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold mr-1">Cover days:</span>
                    <Button
                        onClick={() => onGenerate(9)}
                        data-testid="generate-9-btn"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-cafe-olive/40 bg-transparent text-cafe-espresso hover:bg-cafe-olive/10 h-8 px-3 text-xs"
                    >
                        <ImageIcon className="h-3 w-3 mr-1.5" /> 9 days · IG cover preview
                    </Button>
                    <Button
                        onClick={() => onGenerate(18)}
                        data-testid="generate-18-btn"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-cafe-olive/40 bg-transparent text-cafe-espresso hover:bg-cafe-olive/10 h-8 px-3 text-xs"
                    >
                        18 days · half-month
                    </Button>
                    <Button
                        onClick={() => onGenerate(30)}
                        data-testid="generate-30-btn"
                        size="sm"
                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-8 px-3 text-xs"
                    >
                        Full 30 days · all carousels
                    </Button>
                </div>
            )}

            {/* Progress bar while generating */}
            {status === "generating" && (
                <div className="mb-4" data-testid="image-progress">
                    <div className="flex justify-between mb-1.5 text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-semibold">
                        <span>AI Images · rendering</span>
                        <span>{ready.length} / {expected} · {progressPct}%</span>
                    </div>
                    <div className="h-1.5 bg-cafe-latte rounded-full overflow-hidden">
                        <div
                            className="h-full bg-cafe-olive transition-all duration-700"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            )}

            {order?.ai_images_error && (status === "failed" || status === "partial") && (
                <div
                    className={`mb-3 px-3 py-2.5 rounded-xl text-xs ${
                        budgetExhausted
                            ? "bg-amber-50 border border-amber-300 text-amber-900"
                            : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                    data-testid="image-error-banner"
                >
                    {budgetExhausted ? (
                        <>
                            <p className="font-semibold mb-1">Emergent LLM Key insufficient balance</p>
                            <p className="opacity-90">
                                Image rendering stopped because your Universal Key ran out of credit. Top up via{" "}
                                <span className="font-medium">Profile → Universal Key → Add Balance</span>, then click{" "}
                                <span className="font-medium">Retry failed</span>.
                            </p>
                        </>
                    ) : (
                        order.ai_images_error
                    )}
                </div>
            )}

            {ready.length > 0 && (
                <div className="grid grid-cols-3 gap-1 sm:gap-2 max-w-md" data-testid="image-grid-preview">
                    {ready.map(img => {
                        const src = img.url.startsWith("http") ? img.url : `${BACKEND}${img.url}`;
                        const slide = img.slide || 1;
                        const isCarouselSlide = slide > 1;
                        return (
                            <figure
                                key={`${img.day}-${slide}`}
                                className={`relative aspect-square overflow-hidden rounded-md bg-cafe-latte ${isCarouselSlide ? "ring-1 ring-cafe-terracotta/40" : ""}`}
                                data-testid={`admin-img-${img.day}-${slide}`}
                            >
                                <img src={src} alt={img.theme || `Day ${img.day} slide ${slide}`} loading="lazy" className="w-full h-full object-cover" />
                                <span className="absolute top-1 left-1 text-[9px] font-mono bg-cafe-espresso/85 text-cafe-cream px-1.5 py-0.5 rounded">
                                    {img.day}{isCarouselSlide ? `·${slide}` : ""}
                                </span>
                            </figure>
                        );
                    })}
                </div>
            )}

            {failed.length > 0 && status !== "generating" && (
                <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                    <p className="text-[11px] text-cafe-mocha/80">
                        {failed.length} image{failed.length > 1 ? "s" : ""} failed
                        {failed[0]?.error ? ` · ${String(failed[0].error).slice(0, 80)}` : ""}
                    </p>
                    {onRetryFailed && (
                        <Button
                            onClick={onRetryFailed}
                            data-testid="retry-failed-images-btn"
                            size="sm"
                            variant="outline"
                            className="rounded-full border-cafe-olive/60 bg-transparent text-cafe-olive hover:bg-cafe-olive/10 h-8 px-3 text-xs"
                        >
                            Retry failed ({failed.length})
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Sora 2 reel videos panel ----------
function VideoPanel({ order, onGenerate, onRetryFailed, onFillMissing }) {
    const status = order?.ai_videos_status || "idle";
    const videos = order?.ai_videos || [];
    const ready = videos.filter(v => v?.status === "ready" && v?.url);
    const failed = videos.filter(v => v?.status === "failed");
    const target = order?.ai_videos_target || 5;
    const expected = Math.max(videos.length, target);
    const missingCount = status !== "generating" && videos.length > 0 ? Math.max(target - ready.length, 0) : 0;
    const progressPct = expected > 0 ? Math.round((ready.length / expected) * 100) : 0;
    const budgetExhausted = videos.some(v => v?.budget_exhausted) ||
        /insufficient.balance/i.test(order?.ai_videos_error || "");

    const Status = () => {
        if (status === "generating") {
            return (
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-terracotta text-cafe-cream animate-pulse">
                    <Loader2 className="h-3 w-3 animate-spin" /> Rendering · {ready.length}/{expected}
                </span>
            );
        }
        if (status === "ready") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-olive text-cafe-cream">All {ready.length} ready</span>;
        }
        if (status === "partial") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-amber-700 text-cafe-cream">Partial · {ready.length}/{expected}</span>;
        }
        if (status === "failed") {
            return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-red-700 text-cafe-cream">Failed</span>;
        }
        return <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-latte text-cafe-mocha">Not generated</span>;
    };

    const canGenerate = status !== "generating";

    return (
        <div className="bg-cafe-cream border border-cafe-terracotta/40 rounded-2xl p-5 mb-6" data-testid="video-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-terracotta font-semibold mb-1 flex items-center gap-2">
                        <Film className="h-3 w-3" /> Reel Videos · AI Cinematic
                    </p>
                    <p className="text-cafe-mocha text-sm">
                        Render vertical 8-second reel videos from this kit's reel scripts. Slow (~5 min each) — pick a count.
                    </p>
                </div>
                <Status />
            </div>

            {/* Fill-missing CTA — a killed batch leaves fewer reels than planned
                (e.g. 1 of 5 after a deploy). Backfill ONLY what's missing —
                the good reels are untouched, no wasted Sora spend. */}
            {canGenerate && missingCount > 0 && (
                <div
                    className="bg-amber-100 border border-amber-400/60 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid="video-fill-missing-cta"
                >
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-amber-800 font-bold mb-1">
                            {missingCount} reel{missingCount > 1 ? "s" : ""} missing from this batch
                        </p>
                        <p className="text-cafe-mocha text-xs leading-relaxed">
                            {ready.length}/{target} planned reels are ready — generation was interrupted (server restart).
                            Backfill only the missing ones; finished reels stay untouched.
                        </p>
                    </div>
                    <Button
                        onClick={() => onFillMissing(missingCount)}
                        data-testid="video-fill-missing-btn"
                        className="rounded-full bg-amber-700 hover:bg-amber-800 text-cafe-cream h-9 px-4 text-xs whitespace-nowrap shrink-0"
                    >
                        <Film className="h-3.5 w-3.5 mr-1.5" /> Fill {missingCount} missing reel{missingCount > 1 ? "s" : ""} · ~{missingCount * 5} min
                    </Button>
                </div>
            )}

            {/* Re-generate CTA — surfaced when reels exist but are broken /
                partial / fully failed. Distinct from the "first time" 3/5/8
                strip so Kaily isn't confused about recovery vs initial gen. */}
            {canGenerate && (status === "ready" || status === "partial" || status === "failed") && (
                <div
                    className="bg-cafe-terracotta/10 border border-cafe-terracotta/30 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid="video-regen-cta"
                >
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-1">
                            {status === "ready" ? "Need to re-render?" : "Recover this batch"}
                        </p>
                        <p className="text-cafe-mocha text-xs leading-relaxed">
                            {status === "ready"
                                ? "Click below to overwrite all 5 reels with a fresh render (~25 min). Use this if videos look broken or you want a different cut."
                                : "Some reels failed. You can re-render the whole batch (slow but clean) or retry only the failed ones below."}
                        </p>
                    </div>
                    <Button
                        onClick={() => onGenerate(5)}
                        data-testid="video-regen-5-btn"
                        className="rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream h-9 px-4 text-xs whitespace-nowrap shrink-0"
                    >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-generate 5 reels
                    </Button>
                </div>
            )}

            {/* Highlight CTA when nothing has been generated yet — easy to miss
                the small "Generate: 3 / 5 / 8" strip below when the panel is
                long, so we surface the most common action (5 reels) here. */}
            {status === "idle" && (
                <div
                    className="bg-cafe-terracotta/10 border border-cafe-terracotta/30 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid="video-empty-cta"
                >
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-1">No reels yet</p>
                        <p className="text-cafe-mocha text-xs">Click below to render 5 vertical reels (~25 min). Each one is hand-reviewed by Kaily.</p>
                    </div>
                    <Button
                        onClick={() => onGenerate(5)}
                        data-testid="video-empty-generate-5"
                        className="rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream h-9 px-4 text-xs whitespace-nowrap shrink-0"
                    >
                        <Film className="h-3.5 w-3.5 mr-1.5" /> Generate 5 Reels
                    </Button>
                </div>
            )}

            {canGenerate && (
                <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-border/60">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold mr-1">Generate:</span>
                    <Button
                        onClick={() => onGenerate(3)}
                        data-testid="generate-3-videos-btn"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-cafe-terracotta/40 bg-transparent text-cafe-espresso hover:bg-cafe-terracotta/10 h-8 px-3 text-xs"
                    >
                        <Film className="h-3 w-3 mr-1.5" /> 3 reels · ~15 min
                    </Button>
                    <Button
                        onClick={() => onGenerate(5)}
                        data-testid="generate-5-videos-btn"
                        size="sm"
                        className="rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream h-8 px-3 text-xs"
                    >
                        5 reels · ~25 min
                    </Button>
                    <Button
                        onClick={() => onGenerate(8)}
                        data-testid="generate-8-videos-btn"
                        size="sm"
                        variant="outline"
                        className="rounded-full border-cafe-terracotta/40 bg-transparent text-cafe-espresso hover:bg-cafe-terracotta/10 h-8 px-3 text-xs"
                    >
                        8 reels · ~40 min
                    </Button>
                </div>
            )}

            {status === "generating" && (
                <div className="mb-4" data-testid="video-progress">
                    <div className="flex justify-between mb-1.5 text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-semibold">
                        <span>AI Reels · rendering</span>
                        <span>{ready.length} / {expected} · {progressPct}%</span>
                    </div>
                    <div className="h-1.5 bg-cafe-latte rounded-full overflow-hidden">
                        <div
                            className="h-full bg-cafe-terracotta transition-all duration-700"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    <p className="text-[10px] text-cafe-mocha/80 italic mt-2">
                        Each reel takes ~5 minutes (sequential). You can leave this tab open or come back later — progress is saved.
                    </p>
                </div>
            )}

            {order?.ai_videos_error && (status === "failed" || status === "partial") && (
                <div
                    className={`mb-3 px-3 py-2.5 rounded-xl text-xs ${
                        budgetExhausted
                            ? "bg-amber-50 border border-amber-300 text-amber-900"
                            : "bg-red-50 border border-red-200 text-red-800"
                    }`}
                    data-testid="video-error-banner"
                >
                    {budgetExhausted ? (
                        <>
                            <p className="font-semibold mb-1">Emergent LLM Key insufficient balance</p>
                            <p className="opacity-90">
                                Reel rendering stopped because your Universal Key ran out of credit. Top up via{" "}
                                <span className="font-medium">Profile → Universal Key → Add Balance</span>, then click{" "}
                                <span className="font-medium">Retry failed</span> below to finish only the failed reels.
                            </p>
                        </>
                    ) : (
                        order.ai_videos_error
                    )}
                </div>
            )}

            {ready.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-xl" data-testid="video-grid-preview">
                    {ready.map(v => {
                        const src = v.url.startsWith("http") ? v.url : `${BACKEND}${v.url}`;
                        return (
                            <figure key={v.reel} className="relative rounded-md overflow-hidden bg-cafe-espresso aspect-[9/16]" data-testid={`admin-video-${v.reel}`}>
                                <video src={src} controls muted preload="metadata" className="w-full h-full object-cover" />
                                <span className="absolute top-1 left-1 text-[9px] font-mono bg-cafe-terracotta/90 text-cafe-cream px-1.5 py-0.5 rounded">
                                    Reel {v.reel}
                                </span>
                            </figure>
                        );
                    })}
                </div>
            )}

            {failed.length > 0 && status !== "generating" && (
                <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                    <p className="text-[11px] text-cafe-mocha/80">
                        {failed.length} reel{failed.length > 1 ? "s" : ""} failed
                        {failed[0]?.error ? ` · ${String(failed[0].error).slice(0, 80)}` : ""}
                    </p>
                    {onRetryFailed && (
                        <Button
                            onClick={onRetryFailed}
                            data-testid="retry-failed-videos-btn"
                            size="sm"
                            variant="outline"
                            className="rounded-full border-cafe-terracotta/60 bg-transparent text-cafe-terracotta hover:bg-cafe-terracotta/10 h-8 px-3 text-xs"
                        >
                            Retry failed ({failed.length})
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Main Editor page ----------
export default function StudioOrderEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [draft, setDraft] = useState(null);
    const [tab, setTab] = useState("brand_voice");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refining, setRefining] = useState(false);
    const [refineInstr, setRefineInstr] = useState("");
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        if (!isStudioAuthed()) { navigate("/studio"); return; }
        load({ initial: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /** Fetch the order. `initial=true` is the first mount load — it sets the
     *  loading spinner and toasts on hard failure. Background polling refreshes
     *  (initial=false) silently swallow transient errors so the user isn't
     *  spammed with "Failed to load order" every 8s during a 30-image render. */
    const load = async ({ initial = false } = {}) => {
        if (initial) setLoading(true);
        try {
            const { data } = await axios.get(`${API}/orders`, { headers: studioHeaders() });
            const found = (data || []).find(o => o.id === id);
            if (!found) {
                if (initial) {
                    toast.error("Order not found");
                    navigate("/studio");
                }
                return;
            }
            setOrder(found);
            setDraft(found.ai_draft || null);
            // Poll while text OR images OR videos are still generating
            const isGenerating = found.ai_status === "generating"
                || found.ai_images_status === "generating"
                || found.ai_videos_status === "generating";
            setPolling(isGenerating);
        } catch (err) {
            if (err?.response?.status === 401) { clearStudioPassword(); navigate("/studio"); return; }
            // Only surface load errors on the FIRST page load. Background polling
            // failures are silent — the next interval tick (8s later) will retry.
            if (initial) toast.error("Failed to load order");
            else console.warn("[StudioOrderEditor] background refresh failed (will retry):", err?.message);
        } finally {
            if (initial) setLoading(false);
        }
    };

    // Polling while AI is generating — uses the silent path (initial=false).
    useEffect(() => {
        if (!polling) return;
        const t = setInterval(() => load({ initial: false }), 8000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [polling]);

    const update = (path, value) => {
        setDraft(prev => setNested(prev || {}, path, value));
    };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/orders/${id}/draft`, { ai_draft: draft }, { headers: studioHeaders() });
            toast.success("Saved");
            load();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    const regenerateAll = async () => {
        if (!window.confirm(`Regenerate the ENTIRE draft for ${order?.customer_name || order?.customer_email || "this order"}? Current edits will be lost.`)) return;
        try {
            await axios.post(`${API}/orders/${id}/regenerate`, {}, { headers: studioHeaders() });
            toast.success("Regenerating — this takes 60-120s");
            setPolling(true);
            load();
        } catch {
            toast.error("Failed to start regeneration");
        }
    };

    /** Recover from a stuck "generating" status that didn't promote to ready
     *  even though every image/video is terminal in the DB. Happens after a
     *  backend container restart killed the bg task mid-flight. */
    const healStatus = async () => {
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/heal_status`, {},
                { headers: studioHeaders() },
            );
            const imgs = data.ai_images_status;
            const vids = data.ai_videos_status;
            toast.success(
                `Status fixed → images=${imgs} · videos=${vids || "n/a"}`,
                { description: "Reload customer's kit page to see assets." },
            );
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Heal failed");
        }
    };

    /** Diagnostic: ask backend to check that every ai_images/ai_videos URL
     *  has a real file on disk. Production uploads ephemeral disk = files
     *  get nuked on pod restart. This tells admin which media is missing
     *  so they can re-trigger Generate just for those, instead of guessing.*/
    const checkFiles = async () => {
        try {
            const { data } = await axios.get(
                `${API}/orders/${id}/file_check`,
                { headers: studioHeaders() },
            );
            const imgMissing = data.images?.missing || 0;
            const vidMissing = data.videos?.missing || 0;
            const imgPresent = data.images?.present || 0;
            const vidPresent = data.videos?.present || 0;
            if (imgMissing === 0 && vidMissing === 0) {
                toast.success(
                    `All files present · ${imgPresent} images + ${vidPresent} videos on disk`,
                );
            } else {
                toast.warning(
                    `${imgMissing} image(s) + ${vidMissing} video(s) missing from disk`,
                    {
                        description: "Likely cause: container restart on production. Re-trigger Generate to rebuild missing files.",
                        duration: 12000,
                    },
                );
                console.warn("[file_check] missing media", data);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "File check failed");
        }
    };

    /** One-click fix for legacy Pro/Starter orders whose calendar has Reel
     *  format days (shouldn't exist on new orders post-Phase 17 tier fix).
     *  Converts every Reel/Story day to Single photo so the customer's Kit
     *  page no longer asks them to film videos they didn't pay for. */
    const convertReelsToImages = async () => {
        if (!window.confirm("Convert all Reel/Story days in this calendar to Single photo? This is for Pro/Starter kits where the customer didn't buy reel videos.")) return;
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/convert_reels_to_images`, {},
                { headers: studioHeaders() },
            );
            if (data.converted === 0) {
                toast.success(data.message);
            } else {
                const fillNote = data.fill_started
                    ? ` AI is rendering ${data.fill_slots} new image(s) — ~${Math.max(2, Math.ceil(data.fill_slots / 3) * 2)} min.`
                    : data.fill_skipped_reason
                        ? ` ${data.fill_skipped_reason}`
                        : "";
                toast.success(`Converted ${data.converted} day(s) to image-only`, {
                    description: `Days affected: ${data.days.join(", ")}.${fillNote}`,
                    duration: 12000,
                });
                if (data.fill_started) setPolling(true);
            }
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Conversion failed");
        }
    };

    /** Surgical backfill — generate ONLY the (day, slide) slots that should
     *  have an image but don't (or aren't ready). Use this when an older
     *  order has gaps from a partial run or post-Reel-conversion. */
    const fillMissingImages = async () => {
        if (!window.confirm("Generate images for any missing/non-ready slots? Existing ready images stay untouched.")) return;
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/fill_missing_images`, {},
                { headers: studioHeaders() },
            );
            if (data.missing === 0) {
                toast.success(data.message || "Nothing to fill — every expected image is ready.");
                return;
            }
            toast.success(`Generating ${data.missing} missing image(s)`, {
                description: `Days: ${(data.days || []).join(", ")}. Page polls until done.`,
                duration: 10000,
            });
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Fill missing failed");
        }
    };

    /** AI auto-score every ready image (Gemini Vision, 1-10). Flags <7 for
     *  manual review and lets Kaily batch-accept the rest. Cost ~$0.03/order. */
    const scoreImages = async (rescoreAll = false) => {
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/score_images`,
                { rescore_all: rescoreAll },
                { headers: studioHeaders() },
            );
            toast.success(`Scoring ${data.to_score} image(s)…`, {
                description: `Gemini will flag <${data.threshold}/10 for review. ~60-90s.`,
                duration: 8000,
            });
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scoring failed");
        }
    };

    const generateImages = async (count = 9) => {
        const hasExisting = (order?.ai_images || []).length > 0;
        const label = count === 30 ? "all 30" : `${count}`;
        if (hasExisting && !window.confirm(`Re-generate ${label} images for ${order?.customer_name || order?.customer_email || "this order"}? Existing images will be overwritten.`)) return;
        try {
            await axios.post(
                `${API}/orders/${id}/generate_images`,
                { count },
                { headers: studioHeaders() },
            );
            const eta = count <= 9 ? "~2 min" : count <= 18 ? "~5 min" : "~8 min";
            toast.success(`AI is rendering ${count} images — ${eta}`);
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to start image generation");
        }
    };

    const retryFailedImages = async () => {
        const failedCount = (order?.ai_images || []).filter(i => i?.status === "failed").length;
        if (!failedCount) {
            toast.error("No failed images to retry");
            return;
        }
        if (!window.confirm(`Retry ${failedCount} failed image${failedCount > 1 ? "s" : ""}? Successful ones stay untouched.`)) return;
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/retry_failed_images`,
                {},
                { headers: studioHeaders() },
            );
            toast.success(`Retrying ${data?.retrying || failedCount} images`);
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to retry images");
        }
    };

    const generateVideos = async (count = 5) => {
        const hasExisting = (order?.ai_videos || []).length > 0;
        // Sora 2 Pro: ~5 min per 8-second vertical video (sequential)
        const etaMin = count * 5;
        if (hasExisting && !window.confirm(`Re-generate ${count} reel videos for ${order?.customer_name || order?.customer_email || "this order"}? Existing videos will be overwritten. (~${etaMin} min)`)) return;
        try {
            await axios.post(
                `${API}/orders/${id}/generate_videos`,
                { count },
                { headers: studioHeaders() },
            );
            toast.success(`AI is rendering ${count} reels — ~${etaMin} min`);
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to start video generation");
        }
    };

    const fillMissingVideos = async (missingCount = 0) => {
        if (!window.confirm(`Backfill ${missingCount || "the"} missing reel${missingCount > 1 ? "s" : ""}? Finished reels stay untouched. (~${(missingCount || 1) * 5} min)`)) return;
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/fill_missing_videos`,
                {},
                { headers: studioHeaders() },
            );
            const n = (data?.missing || []).length;
            toast.success(n ? `Rendering missing reels ${(data.missing).join(", ")} — ~${n * 5} min` : "Nothing missing — all reels ready");
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to start backfill");
        }
    };

    const retryFailedVideos = async () => {
        const failedCount = (order?.ai_videos || []).filter(v => v?.status === "failed").length;
        if (!failedCount) {
            toast.error("No failed reels to retry");
            return;
        }
        if (!window.confirm(`Retry ${failedCount} failed reel${failedCount > 1 ? "s" : ""}? Successful reels stay untouched. (~${failedCount * 5} min)`)) return;
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/retry_failed_videos`,
                {},
                { headers: studioHeaders() },
            );
            toast.success(`Retrying reels ${(data?.retrying || []).join(", ")} — ~${(data?.retrying || []).length * 5} min`);
            setPolling(true);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to retry videos");
        }
    };

    const refineCurrent = async () => {
        if (!refineInstr.trim()) {
            toast.error("Tell the AI what to change");
            return;
        }
        setRefining(true);
        try {
            const { data } = await axios.post(
                `${API}/orders/${id}/refine`,
                { section: tab, instruction: refineInstr.trim() },
                { headers: studioHeaders() },
            );
            setDraft(prev => ({ ...prev, [tab]: data.value }));
            setRefineInstr("");
            toast.success(`Refined "${tab}"`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Refine failed");
        } finally {
            setRefining(false);
        }
    };

    const deliver = async () => {
        if (!window.confirm("Deliver this kit to the customer? They will receive an email with the public kit link.")) return;
        try {
            await axios.post(`${API}/orders/${id}/deliver`, {}, {
                headers: studioHeaders(),
                timeout: 60000, // delivery + email send can take 30-40s on cold Resend
            });
            toast.success("Delivered + email sent");
            navigate("/studio");
        } catch (err) {
            // Backend may have actually succeeded even if axios times out.
            // Reload the order to check the real state.
            try {
                const { data } = await axios.get(`${API}/orders`, { headers: studioHeaders() });
                const found = (data || []).find(o => o.id === id);
                if (found?.status === "delivered") {
                    toast.success("Delivered + email sent (after slow response)");
                    navigate("/studio");
                    return;
                }
            } catch { /* ignore */ }
            toast.error(err?.response?.data?.detail || "Delivery failed — please retry");
        }
    };

    const intake = order?.intake;
    const aiStatus = order?.ai_status || "idle";

    const sectionContent = useMemo(() => {
        if (!draft) return null;
        if (tab === "brand_voice") return <BrandVoiceEditor data={draft.brand_voice} onChange={update} />;
        if (tab === "calendar_30d") return (
            <CalendarPreviewEditor
                data={draft.calendar_30d}
                onChange={update}
                orderId={id}
                images={order?.ai_images || []}
                onImagesRefresh={() => load()}
            />
        );
        if (tab === "captions") return <CaptionsEditor data={draft.captions} onChange={update} />;
        if (tab === "hooks") return <HooksEditor data={draft.hooks} onChange={update} />;
        if (tab === "reels") return <ReelsEditor data={draft.reels} onChange={update} />;
        if (tab === "visual_guide") return <VisualGuideEditor data={draft.visual_guide} onChange={update} />;
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, draft]);

    if (loading) return <div className="min-h-screen bg-cafe-cream flex items-center justify-center text-cafe-mocha">Loading…</div>;

    return (
        <div className="min-h-screen bg-cafe-cream flex flex-col">
            <SiteNav />
            <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 pt-24 sm:pt-28 pb-12 w-full" data-testid="order-editor">
                {/* Top toolbar — back nav + emergency actions. Plain block
                    (not sticky) so it never collides with the site's main
                    navigation bar. Renders in a single visible strip so all
                    recovery actions sit together where Kaily expects them. */}
                <div className="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 mb-6 bg-cafe-cream border-y sm:border border-cafe-olive/20 sm:rounded-2xl flex flex-wrap items-center justify-between gap-3">
                    <Link
                        to="/studio"
                        data-testid="back-to-studio-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-olive/10 hover:bg-cafe-olive/20 text-cafe-olive text-xs font-semibold uppercase tracking-[0.12em] transition-colors"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back to Studio
                    </Link>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Stuck-text recovery — visible whenever ai_status is generating */}
                        {order?.ai_status === "generating" && (
                            <Button
                                onClick={healStatus}
                                variant="outline"
                                title="The AI text generation appears stuck. Click to promote status to 'ready' if the draft is already populated. Safe: never destroys data."
                                data-testid="fix-ai-writing-btn"
                                className="rounded-full border-amber-700/50 text-amber-800 bg-amber-50 hover:bg-amber-100 h-9 px-4 text-xs font-semibold uppercase tracking-[0.1em]"
                            >
                                <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Fix AI Writing
                            </Button>
                        )}
                        {/* Convert Reel-format days → Single photo. Shown
                            for any order EXCEPT Premium (which paid for reel
                            videos). Pro/Starter/blank product_name all qualify
                            because they didn't buy reels, so reel days in
                            their calendar are wrong. Hidden when calendar has
                            no reel days. */}
                        {(() => {
                            const pname = (order?.product_name || "").toLowerCase();
                            const isPremium = pname.includes("premium");
                            const calendar = (order?.ai_draft || {}).calendar_30d || [];
                            const reelCount = calendar.filter(d => {
                                const f = (d.format || "").toLowerCase();
                                return f.startsWith("reel") || f.startsWith("story");
                            }).length;
                            if (isPremium || reelCount === 0) return null;
                            return (
                                <Button
                                    onClick={convertReelsToImages}
                                    title={`This kit has ${reelCount} Reel/Story day(s) but it's not a Premium tier. Convert them all to Single-photo days.`}
                                    data-testid="convert-reels-btn"
                                    className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs font-semibold uppercase tracking-[0.1em]"
                                >
                                    <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Fix Reels ({reelCount} days) → Image
                                </Button>
                            );
                        })()}
                        {/* Fill missing images — surgical backfill for orders
                            where some image-format days have NO ready render
                            (legacy reel conversions, partial crashes, etc.).
                            Hidden when nothing is missing. Available even on
                            Premium orders since reel days are skipped. */}
                        {(() => {
                            const calendar = (order?.ai_draft || {}).calendar_30d || [];
                            if (!calendar.length) return null;
                            const imgs = order?.ai_images || [];
                            const readyKey = new Set(
                                imgs.filter(i => i?.status === "ready").map(i => `${i.day}-${i.slide || 1}`)
                            );
                            let missing = 0;
                            calendar.forEach((d, idx) => {
                                const f = (d.format || "single photo").toLowerCase();
                                const dayIdx = idx + 1;
                                const slides = f === "carousel" ? [1, 2, 3, 4, 5]
                                    : f === "single photo" ? [1]
                                        : [];
                                slides.forEach(s => {
                                    if (!readyKey.has(`${dayIdx}-${s}`)) missing += 1;
                                });
                            });
                            if (missing === 0) return null;
                            const generating = order?.ai_images_status === "generating";
                            return (
                                <Button
                                    onClick={fillMissingImages}
                                    disabled={generating}
                                    title={`${missing} image slot(s) are missing or not ready. Click to render only those — existing images stay.`}
                                    data-testid="fill-missing-images-btn"
                                    className="rounded-full bg-cafe-espresso hover:bg-black text-cafe-cream h-9 px-4 text-xs font-semibold uppercase tracking-[0.1em] disabled:opacity-50"
                                >
                                    <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${generating ? "animate-spin" : ""}`} /> Fill {missing} Missing
                                </Button>
                            );
                        })()}
                        {/* AI auto-score images — flags <7/10 for human review */}
                        {(() => {
                            const imgs = order?.ai_images || [];
                            const readyCount = imgs.filter(i => i.status === "ready" && i.url).length;
                            if (readyCount === 0) return null;
                            const scoreStatus = order?.ai_score_status || "idle";
                            const summary = order?.ai_score_summary;
                            const isScoring = scoreStatus === "scoring";
                            const hasScores = summary && summary.total > 0;
                            const label = isScoring
                                ? "Scoring…"
                                : hasScores
                                    ? `★ ${summary.avg_score}/10 (${summary.review} flag)`
                                    : `★ Score ${readyCount} images`;
                            const title = isScoring
                                ? "Gemini Vision is rating each image 1-10. Score < 7 → flagged for review."
                                : hasScores
                                    ? `${summary.accept}/${summary.total} accepted, ${summary.review} need review (avg ${summary.avg_score}/10). Click to re-score.`
                                    : `Auto-score all ${readyCount} ready images. Gemini flags any <7/10 for your review. ~60s, ~$0.03 total.`;
                            return (
                                <Button
                                    onClick={() => scoreImages(hasScores)}
                                    disabled={isScoring}
                                    title={title}
                                    data-testid="score-images-btn"
                                    className={`rounded-full h-9 px-4 text-xs font-semibold uppercase tracking-[0.1em] disabled:opacity-50 ${hasScores
                                        ? (summary.review > 0
                                            ? "bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream"
                                            : "bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream")
                                        : "bg-white border border-cafe-olive/40 text-cafe-olive hover:bg-cafe-olive/10"
                                        }`}
                                >
                                    {isScoring ? (
                                        <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    ) : null}
                                    {label}
                                </Button>
                            );
                        })()}
                    </div>
                </div>

                {/* Legacy below-header back link removed in favour of the sticky bar above. */}

                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.3em] text-cafe-olive mb-1">Kit Editor</p>
                        <h1 className="font-serif text-4xl text-cafe-espresso">{intake?.cafe_name || order?.customer_name || order?.customer_email}</h1>
                        <p className="text-cafe-mocha text-sm mt-1">
                            {order?.customer_email} · @{intake?.ig_handle?.replace(/.*\//, "") || "—"} · {intake?.location || "—"}
                        </p>
                        <div className="mt-1.5">
                            <TransferOrder orderId={id} currentEmail={order?.customer_email} onDone={load} />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <StatusBadge status={aiStatus} />
                        {order?.status === "delivered" && (
                            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded-full bg-cafe-olive text-cafe-cream">
                                Delivered
                            </span>
                        )}
                    </div>
                </div>

                {aiStatus === "generating" && (
                    <div className="bg-cafe-latte border border-border rounded-2xl p-6 mb-6 flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-cafe-olive" />
                        <div>
                            <div className="font-serif text-cafe-espresso">Claude is writing the first draft…</div>
                            <p className="text-cafe-mocha text-sm">This usually takes 60-120 seconds. Page auto-refreshes.</p>
                        </div>
                    </div>
                )}

                {aiStatus === "failed" && (
                    <div className="bg-red-50 border border-red-300 rounded-2xl p-5 mb-6 flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-700 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-semibold text-red-800">AI generation failed</div>
                            <p className="text-red-700 text-sm mt-1">{order?.ai_error || "Unknown error"}</p>
                            <Button onClick={regenerateAll} className="mt-3 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-9 px-4 text-xs">
                                <RefreshCw className="h-4 w-4 mr-1.5" /> Retry generation
                            </Button>
                        </div>
                    </div>
                )}

                {aiStatus === "idle" && !draft && (
                    <div className="bg-cafe-latte border border-dashed border-border rounded-2xl p-10 text-center mb-6">
                        <Sparkles className="h-7 w-7 text-cafe-olive mx-auto mb-2" />
                        <div className="font-serif text-2xl text-cafe-espresso mb-1">No AI draft yet</div>
                        <p className="text-cafe-mocha text-sm mb-4">Generate the first draft from the customer's brief.</p>
                        <Button onClick={regenerateAll} disabled={!intake} className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream">
                            <Sparkles className="h-4 w-4 mr-1.5" /> Generate AI draft
                        </Button>
                    </div>
                )}

                {draft && (
                    <>
                        {/* Product Vision — gives Kaily a SAFETY NET to verify and edit
                            how AI sees each product BEFORE the image and reel render passes use the
                            description in renders. Highest leverage for product accuracy. */}
                        <ProductVisionPanel order={order} studioHeaders={studioHeaders} onReload={load} />

                        {/* IG 9-Grid (Nano Banana) panel */}
                        <ImagePanel
                            order={order}
                            onGenerate={generateImages}
                            onRetryFailed={retryFailedImages}
                        />

                        {/* Reel Videos (Sora 2) panel */}
                        <VideoPanel
                            order={order}
                            onGenerate={generateVideos}
                            onRetryFailed={retryFailedVideos}
                            onFillMissing={fillMissingVideos}
                        />

                        {/* Section tabs */}
                        <div className="border-b border-border mb-6 flex flex-wrap gap-1 overflow-x-auto">
                            {SECTION_TABS.map(s => (
                                <button
                                    key={s.key}
                                    onClick={() => setTab(s.key)}
                                    data-testid={`tab-${s.key}`}
                                    className={cn(
                                        "px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap",
                                        tab === s.key
                                            ? "border-cafe-olive text-cafe-espresso"
                                            : "border-transparent text-cafe-mocha hover:text-cafe-espresso",
                                    )}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>

                        {/* Refine box (per-section) */}
                        <div className="bg-cafe-cream border border-cafe-olive/30 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-3 items-start md:items-center">
                            <Wand2 className="h-4 w-4 text-cafe-olive shrink-0 hidden md:block" />
                            <Input
                                value={refineInstr}
                                onChange={e => setRefineInstr(e.target.value)}
                                placeholder={`Tell AI how to refine this section… e.g. "Make the hooks more playful, less corporate"`}
                                className="flex-1 bg-cafe-latte border-border rounded-full text-sm"
                                data-testid="refine-instruction"
                            />
                            <Button
                                onClick={refineCurrent}
                                disabled={refining || !refineInstr.trim()}
                                data-testid="refine-btn"
                                className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5 text-xs"
                            >
                                {refining ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                                Refine "{SECTION_TABS.find(s => s.key === tab)?.label}"
                            </Button>
                        </div>

                        {/* The section content */}
                        <div className="bg-cafe-latte/50 border border-border rounded-2xl p-6 mb-8">
                            {sectionContent}
                        </div>

                        {/* Bottom action bar */}
                        <div className="sticky bottom-4 bg-cafe-cream/90 backdrop-blur border border-border rounded-2xl sm:rounded-full p-2 flex flex-wrap gap-2 items-center justify-between shadow-lg">
                            <div className="hidden sm:flex gap-2 items-center pl-3 text-xs text-cafe-mocha">
                                {order?.deliverable_url && (
                                    <a href={order.deliverable_url} target="_blank" rel="noreferrer" className="text-cafe-olive hover:text-cafe-oliveDark inline-flex items-center gap-1">
                                        <ExternalLink className="h-3 w-3" /> Live kit
                                    </a>
                                )}
                            </div>
                            <div className="flex gap-2 flex-wrap justify-end w-full sm:w-auto">
                                {/* Emergency recovery — visible when at least one media status is stuck on 'generating' */}
                                {(order?.ai_images_status === "generating" || order?.ai_videos_status === "generating") && (
                                    <Button
                                        onClick={healStatus}
                                        variant="outline"
                                        title="Use this if status has been stuck on 'rendering' for more than 5 min after the count hit 100%. This promotes the status to 'ready' if all items in the array are already terminal."
                                        data-testid="heal-status-btn"
                                        className="rounded-full border-amber-700/50 text-amber-800 bg-amber-50 hover:bg-amber-100 h-10 px-4 text-xs"
                                    >
                                        <AlertTriangle className="h-4 w-4 mr-1" /> Fix stuck status
                                    </Button>
                                )}
                                <Button
                                    onClick={checkFiles}
                                    variant="outline"
                                    title="Check that every image / video URL has a real file on disk. Use this when the kit page renders empty placeholders despite status=ready."
                                    data-testid="check-files-btn"
                                    className="rounded-full border-cafe-espresso/20 text-cafe-mocha bg-transparent hover:bg-cafe-latte h-10 px-4 text-xs"
                                >
                                    Check files
                                </Button>
                                <Button
                                    onClick={regenerateAll}
                                    variant="outline"
                                    className="rounded-full border-cafe-espresso/20 text-cafe-mocha bg-transparent hover:bg-cafe-latte h-10 px-4 text-xs"
                                    data-testid="regenerate-all"
                                >
                                    <RefreshCw className="h-4 w-4 mr-1" /> Regenerate all
                                </Button>
                                <Button
                                    onClick={save}
                                    disabled={saving}
                                    data-testid="save-draft"
                                    variant="outline"
                                    className="rounded-full border-cafe-olive text-cafe-olive bg-transparent hover:bg-cafe-olive/10 h-10 px-4 text-xs"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                                    Save
                                </Button>
                                {order?.status !== "delivered" && (
                                    <Button
                                        onClick={deliver}
                                        data-testid="deliver-kit"
                                        className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5 text-xs"
                                    >
                                        <Send className="h-4 w-4 mr-1" /> Save & Deliver
                                    </Button>
                                )}
                                {order?.status === "delivered" && (
                                    <span className="text-xs text-cafe-olive font-semibold inline-flex items-center gap-1 px-3">
                                        <CheckCircle2 className="h-4 w-4" /> Delivered
                                    </span>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>
            <SiteFooter />
        </div>
    );
}
